/**
 * Stub responses for the Falcon engine SDK.
 *
 * Wired in via createFalconClient({ stubMode: true }). Lets the
 * mini-app + tests run end-to-end before the real /v1/* namespace
 * lands on falcon.ai/telegram-mini-app.
 *
 * Shapes are the canonical types — anything Falcon's real /v1/*
 * eventually returns must match these.
 */

import type {
  EdgeCall,
  EdgeDiscovery,
  Match,
  MatchState,
  Persona,
  PersonaKey,
  PersonaStats,
  DebateResponse,
} from '@sharp8/types';

// ── Teams ──────────────────────────────────────────────────────────

function team(id: string, name: string, shortName: string, espnId: string, color: string) {
  return {
    id,
    name,
    shortName,
    crestUrl: `https://a.espncdn.com/i/teamlogos/soccer/500/${espnId}.png`,
    primaryColor: color,
  };
}

const TEAMS = {
  arsenal:   team('arsenal',   'Arsenal',         'Arsenal',  '359', '#EF0107'),
  chelsea:   team('chelsea',   'Chelsea',         'Chelsea',  '363', '#034694'),
  liverpool: team('liverpool', 'Liverpool',       'Liverpool','364', '#C8102E'),
  manutd:    team('manutd',    'Manchester Utd',  'Man Utd',  '360', '#DA291C'),
  real:      team('real',      'Real Madrid',     'Real',     '86',  '#FEBE10'),
  barca:     team('barca',     'Barcelona',       'Barça',    '83',  '#A50044'),
  kashima:   team('kashima',   'Kashima Antlers', 'Kashima',  '322', '#9F1F32'),
  urawa:     team('urawa',     'Urawa Red Diamonds','Urawa',  '291', '#E60012'),
};

const LEAGUES = {
  epl:    { id: 'epl',    name: 'Premier League',    shortName: 'EPL',     badgeUrl: 'https://a.espncdn.com/i/leaguelogos/soccer/500/23.png' },
  laliga: { id: 'laliga', name: 'La Liga',           shortName: 'La Liga', badgeUrl: 'https://a.espncdn.com/i/leaguelogos/soccer/500/15.png' },
  jl:     { id: 'jl',     name: 'J-League',          shortName: 'J-League',badgeUrl: '' },
};

// ── Matches ────────────────────────────────────────────────────────

function nowMinus(seconds: number): string {
  return new Date(Date.now() - seconds * 1000).toISOString();
}

const M_ARS_CHE: Match = {
  id: 'epl-arsenal-chelsea-2026-05-22',
  league: LEAGUES.epl,
  home: TEAMS.arsenal,
  away: TEAMS.chelsea,
  kickoff: nowMinus(60 * 67), // started 67 min ago — currently live
  state: 'live',
  score: { home: 1, away: 0, minute: "67'" },
  stateChangedAt: nowMinus(60 * 67),
};

const M_LIV_MUN: Match = {
  id: 'epl-liverpool-manutd-2026-05-23',
  league: LEAGUES.epl,
  home: TEAMS.liverpool,
  away: TEAMS.manutd,
  kickoff: new Date(Date.now() + 1000 * 60 * 60 * 4).toISOString(),
  state: 'pre',
  score: null,
  stateChangedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
};

const M_REAL_BARCA: Match = {
  id: 'laliga-real-barca-2026-05-25',
  league: LEAGUES.laliga,
  home: TEAMS.real,
  away: TEAMS.barca,
  kickoff: new Date(Date.now() + 1000 * 60 * 60 * 36).toISOString(),
  state: 'pre',
  score: null,
  stateChangedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
};

const M_KASHIMA_URAWA: Match = {
  id: 'jleague-kashima-urawa-2026-05-26',
  league: LEAGUES.jl,
  home: TEAMS.kashima,
  away: TEAMS.urawa,
  kickoff: new Date(Date.now() + 1000 * 60 * 60 * 50).toISOString(),
  state: 'pre',
  score: null,
  stateChangedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
};

const MATCHES: Record<string, Match> = {
  [M_ARS_CHE.id]: M_ARS_CHE,
  [M_LIV_MUN.id]: M_LIV_MUN,
  [M_REAL_BARCA.id]: M_REAL_BARCA,
  [M_KASHIMA_URAWA.id]: M_KASHIMA_URAWA,
};

// ── Edge calls ─────────────────────────────────────────────────────

const E_ARS_CHE: EdgeCall = {
  matchId: M_ARS_CHE.id,
  market: 'AH',
  line: 'Arsenal −0.75',
  price: 1.95,
  book: 'Bet365',
  ev: 0.14,
  freshness: 'alive',
  verifiedAt: nowMinus(120),
  reasoning: {
    body:
      'Arsenal home xG 2.10 over last 8 vs Chelsea away xG 0.82 (n=15). ' +
      'Chelsea CB pair Disasi + Tosin both out, Badiashile carrying knock from Tuesday — ' +
      'defensive shape compromised at the break. 72% of recent volume on home; Pinnacle ' +
      'line shorter than consensus signals sharp money. Second-opinion model (FiveThirtyEight ' +
      'SPI) agrees on direction, disagrees on margin (−0.5 vs −0.75).',
    citations: [
      { source: 'asianbandar.net', url: null, verifiedAt: nowMinus(120) },
      { source: 'FiveThirtyEight SPI', url: null, verifiedAt: nowMinus(60 * 60) },
    ],
  },
  panel: {
    backing: 5,
    total: 6,
    dissent: [
      {
        personaKey: 'caleb',
        alternativeLine: 'Chelsea +0.75',
        reasoning: 'Eze back to fitness — Chelsea midfield more solid than xG suggests.',
      },
    ],
  },
};

const E_LIV_MUN: EdgeCall = {
  matchId: M_LIV_MUN.id,
  market: 'AH',
  line: 'Liverpool −0.5',
  price: 1.92,
  book: 'Pinnacle',
  ev: 0.09,
  freshness: 'alive',
  verifiedAt: nowMinus(180),
  reasoning: {
    body:
      'Liverpool xG 2.4 last 5 home, Man Utd shipping 1.8 away. Anfield record vs Utd: ' +
      '4-1-0 in last 5. Utd missing Casemiro + Mainoo, midfield press will be exposed.',
    citations: [{ source: 'asianbandar.net', url: null, verifiedAt: nowMinus(180) }],
  },
  panel: { backing: 4, total: 6, dissent: [] },
};

const E_REAL_BARCA: EdgeCall = {
  matchId: M_REAL_BARCA.id,
  market: 'OU',
  line: 'Over 2.75',
  price: 1.95,
  book: 'Bet365',
  ev: 0.07,
  freshness: 'alive',
  verifiedAt: nowMinus(240),
  reasoning: {
    body:
      'El Clásico last 6 averaged 3.4 goals; both sides scored in 5. Real attacking form + ' +
      "Barça's high press inviting transitions. Combined xG > 3.0 baseline before kickoff.",
    citations: [{ source: 'asianbandar.net', url: null, verifiedAt: nowMinus(240) }],
  },
  panel: { backing: 5, total: 6, dissent: [] },
};

const E_KASHIMA_URAWA: EdgeCall = {
  matchId: M_KASHIMA_URAWA.id,
  market: 'AH',
  line: 'Kashima +0.5',
  price: 1.78,
  book: 'SBOBET',
  ev: 0.06,
  freshness: 'tightened',
  verifiedAt: nowMinus(360),
  reasoning: {
    body:
      "Kashima unbeaten in last 7 home. Urawa missing 2 starters in midfield. +0.5 line " +
      'gives draw-no-bet cushion — sharp money has been on the home side for 48h.',
    citations: [{ source: 'asianbandar.net', url: null, verifiedAt: nowMinus(360) }],
  },
  panel: { backing: 3, total: 6, dissent: [] },
};

const EDGES_BY_MATCH: Record<string, EdgeCall[]> = {
  [M_ARS_CHE.id]:        [E_ARS_CHE],
  [M_LIV_MUN.id]:        [E_LIV_MUN],
  [M_REAL_BARCA.id]:     [E_REAL_BARCA],
  [M_KASHIMA_URAWA.id]:  [E_KASHIMA_URAWA],
};

const DISCOVERY: EdgeDiscovery = {
  hero: E_ARS_CHE,
  more: [E_LIV_MUN, E_REAL_BARCA, E_KASHIMA_URAWA],
  mostContested: [],
};

// ── Personas ───────────────────────────────────────────────────────

const PERSONAS: Persona[] = [
  { key: 'sharp8-house', name: 'Sharp8 House', tagline: 'The synthesis voice',           color: '#4ED5DB', portraitUrl: '', accuracy: 0.62 },
  { key: 'tanaka',       name: 'Tanaka',       tagline: 'Statistical sharp',             color: '#1A3A6B', portraitUrl: '', accuracy: 0.58 },
  { key: 'sofia',        name: 'Sofia',        tagline: 'Intuitive trader',              color: '#FF6B9D', portraitUrl: '', accuracy: 0.55 },
  { key: 'arjun',        name: 'Arjun',        tagline: 'Quantitative analyst',          color: '#2D6B47', portraitUrl: '', accuracy: 0.60 },
  { key: 'caleb',        name: 'Caleb',        tagline: 'Contrarian sharp',              color: '#D4831F', portraitUrl: '', accuracy: 0.51 },
  { key: 'lucas',        name: 'Lucas',        tagline: 'European tactical eye',         color: '#6B47B8', portraitUrl: '', accuracy: 0.57 },
];

const PERSONA_STATS: Record<PersonaKey, PersonaStats> = {
  'sharp8-house': { key: 'sharp8-house', accuracy: 0.62, last30: { wins: 18, losses: 11, pushes: 1, units: 6.4 }, streak: 3 },
  'tanaka':       { key: 'tanaka',       accuracy: 0.58, last30: { wins: 16, losses: 13, pushes: 1, units: 2.1 }, streak: 1 },
  'sofia':        { key: 'sofia',        accuracy: 0.55, last30: { wins: 14, losses: 14, pushes: 2, units: 0.8 }, streak: -1 },
  'arjun':        { key: 'arjun',        accuracy: 0.60, last30: { wins: 17, losses: 12, pushes: 1, units: 4.2 }, streak: 2 },
  'caleb':        { key: 'caleb',        accuracy: 0.51, last30: { wins: 13, losses: 16, pushes: 1, units: -2.4 }, streak: -3 },
  'lucas':        { key: 'lucas',        accuracy: 0.57, last30: { wins: 15, losses: 14, pushes: 1, units: 1.6 }, streak: 1 },
};

// ── Stub response router ───────────────────────────────────────────

export const stubs = {
  edgesToday: (opts?: { league?: string; limit?: number }): EdgeDiscovery => {
    let more = DISCOVERY.more;
    if (opts?.league) {
      more = more.filter((e) => MATCHES[e.matchId]?.league.id === opts.league);
    }
    if (opts?.limit !== undefined) {
      more = more.slice(0, Math.max(0, opts.limit - 1));
    }
    return { ...DISCOVERY, more };
  },

  edgesForMatch: (matchId: string): EdgeCall[] => EDGES_BY_MATCH[matchId] ?? [],

  matchGet: (matchId: string): Match => {
    const m = MATCHES[matchId];
    if (!m) throw new Error(`No stub match with id ${matchId}`);
    return m;
  },

  matchState: (matchId: string): MatchState => {
    const m = MATCHES[matchId];
    if (!m) throw new Error(`No stub match with id ${matchId}`);
    return m.state;
  },

  matchPreview: (matchId: string, _lang?: string): { body: string } => {
    const edges = EDGES_BY_MATCH[matchId];
    return { body: edges?.[0]?.reasoning.body ?? 'No preview available for this match.' };
  },

  personasList: (): Persona[] => PERSONAS,

  personasStats: (key: PersonaKey): PersonaStats => {
    const stats = PERSONA_STATS[key];
    if (!stats) throw new Error(`No stub stats for persona ${key}`);
    return stats;
  },

  personasPortrait: (key: PersonaKey): { url: string } => {
    const p = PERSONAS.find((x) => x.key === key);
    return { url: p?.portraitUrl ?? '' };
  },

  debateAsk: (matchId: string, query: string, _lang?: string): DebateResponse => {
    const edge = EDGES_BY_MATCH[matchId]?.[0];
    if (!edge) {
      return {
        replies: [],
        refused: true,
        refusalReason: 'No edge data for this match — the engine refuses to predict.',
      };
    }
    // Single stub reply from Falcon House referring back to the synthesis.
    return {
      replies: [
        {
          id: `stub-${Date.now()}`,
          matchId,
          role: 'persona',
          personaKey: 'sharp8-house',
          content:
            `Re: "${query.slice(0, 60)}${query.length > 60 ? '…' : ''}" — ` +
            edge.reasoning.body.slice(0, 200) + '…',
          citations: edge.reasoning.citations.slice(0, 1),
          createdAt: new Date().toISOString(),
        },
      ],
      refused: false,
      refusalReason: null,
    };
  },
};
